import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"
import { usePage } from "@inertiajs/react"
import { Transition } from "@headlessui/react"
import { useIntl } from "react-intl"
import { CircleAlert, CircleCheck, X } from "lucide-react"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

type Toast = { id: number; title: string; body?: string; tone: "success" | "error" }
type Notify = (toast: Omit<Toast, "id" | "tone"> & { tone?: Toast["tone"] }) => void

const ToastContext = createContext<Notify>(() => {})

export const useToast = () => useContext(ToastContext)

let nextId = 1

/**
 * Application UI "simple notification": a panel that slides in at the bottom
 * right, clear of the page's action buttons. Notices go away on their own; errors stay until dismissed. Wrap the
 * app once; call useToast() anywhere, or let FlashToaster turn Rails flash
 * messages into toasts.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), [])
  const notify = useCallback<Notify>(
    ({ tone = "success", ...toast }) => setToasts((list) => [...list, { id: nextId++, tone, ...toast }]),
    [],
  )

  return (
    <ToastContext.Provider value={notify}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-0 z-50 flex items-end px-4 pt-6 pb-20 sm:px-6">
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {toasts.map((toast) => (
            <ToastPanel key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  )
}

function ToastPanel({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const intl = useIntl()
  const [show, setShow] = useState(false)
  useEffect(() => {
    setShow(true)
    if (toast.tone === "error") return
    const timer = setTimeout(() => setShow(false), 5000)
    return () => clearTimeout(timer)
  }, [toast.tone])

  const Icon = toast.tone === "error" ? CircleAlert : CircleCheck

  return (
    <Transition show={show} afterLeave={onDismiss}>
      <div
        role={toast.tone === "error" ? "alert" : "status"}
        className="pointer-events-auto w-full max-w-sm rounded-xl bg-page shadow-lg ring-1 ring-hairline transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in sm:data-closed:data-enter:translate-x-2 sm:data-closed:data-enter:translate-y-0"
      >
        <div className="flex items-start p-4">
          <Icon aria-hidden="true" className={cn("size-6 shrink-0", toast.tone === "error" ? "text-danger" : "text-accent")} />
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-ink-display">{toast.title}</p>
            {toast.body && <p className="mt-1 text-sm text-ink-muted">{toast.body}</p>}
          </div>
          <button
            type="button"
            onClick={() => setShow(false)}
            className="ml-4 inline-flex shrink-0 cursor-pointer rounded-md text-ink-muted hover:text-ink-display focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <span className="sr-only">{intl.formatMessage({ id: "toast.close" })}</span>
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
      </div>
    </Transition>
  )
}

/** Shows the page's Rails flash notice and alert as toasts, once per response. */
export function FlashToaster() {
  const flash = usePage<PageProps>().props.flash
  const notify = useToast()

  useEffect(() => {
    if (flash?.notice) notify({ title: flash.notice })
    if (flash?.alert) notify({ title: flash.alert, tone: "error" })
    // Each Inertia response brings a fresh flash object, so repeats of the same message still show.
  }, [flash, notify])

  return null
}
